import React from "react";

/**
 * The four ways the Broadcasts list can have nothing to show.
 *
 * ---------------------------------------------------------------------------
 * WHY FOUR STATES AND NOT ONE
 * ---------------------------------------------------------------------------
 * "Nothing here" is four different facts wearing the same clothes. A first run
 * means *make one*; a filtered miss means *widen the net*; a forbidden list
 * means *ask someone*; an error means *try again*. Collapsing them tells a new
 * user they have a filter problem and an experienced user they have no data —
 * both wrong, and both dead ends.
 *
 * ---------------------------------------------------------------------------
 * WHY NOT `src/common/comms/EmptyState.jsx`
 * ---------------------------------------------------------------------------
 * Its shape fits (title / hint / action) but its skin does not: it hard-codes
 * `text-lg font-bold text-gray-700` for the title and `text-gray-400` for the
 * hint, with no way to override either, while this console's empty states are
 * specified as `text-sm font-medium text-bc-text` over `text-bc-soft`. It also
 * defaults to an `FiInbox` illustration, and the spec is explicit that the
 * first-run state has no illustration. Passing `icon={null}` would suppress the
 * icon but not the greys, so this is local rather than a wrapper fighting its
 * own base.
 *
 * Presentational only: no fetching, no copy generation. The `error` variant
 * renders `message` verbatim because the hook has already put it through the
 * spec's copy rules.
 */

/** Shared button geometry. One literal so Tailwind keeps it. */
const BUTTON_BASE =
  "inline-flex items-center rounded-lg px-3 py-1.5 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary focus-visible:ring-offset-2";

const PRIMARY_BUTTON = "bg-orange-primary text-white hover:bg-[#E85A10]";

const SECONDARY_BUTTON =
  "border border-bc-border bg-white text-bc-text-2 hover:bg-bc-subtle";

/**
 * Copy per variant. `forbidden` carries no action on purpose: offering a button
 * that cannot succeed is worse than offering none.
 */
const VARIANTS = {
  "first-run": {
    title: "No broadcasts yet.",
    hint: "Send a push or an email to a segment of your app users.",
  },
  filtered: {
    title: "No broadcasts match these filters.",
    hint: "Try a wider date range, or a different status.",
  },
  forbidden: {
    title: "You need manager access to view broadcasts.",
  },
  error: {
    title: "Something went wrong.",
  },
};

/** Anything unrecognised states the plain fact and offers nothing. */
const FALLBACK = { title: "No broadcasts." };

/**
 * The Broadcasts empty / unavailable state.
 *
 * @param {Object} props
 * @param {'first-run'|'filtered'|'forbidden'|'error'} props.variant - Which fact is being reported.
 * @param {() => void} [props.onCreate] - `first-run` only: starts a new broadcast.
 * @param {() => void} [props.onClear] - `filtered` only: drops the active filters.
 * @param {() => void} [props.onRetry] - `error` only: refetches the list.
 * @param {string} [props.message] - `error` only: the already-formatted message, rendered verbatim.
 * @param {string} [props.className] - Extra classes.
 * @returns {React.ReactElement}
 */
export const BroadcastEmpty = ({
  variant,
  onCreate,
  onClear,
  onRetry,
  message,
  className = "",
}) => {
  const copy = VARIANTS[variant] || FALLBACK;

  // The error variant's own text always wins: the hook wrote it for this
  // failure, and a generic heading would bury it.
  const title = variant === "error" && message ? message : copy.title;

  return (
    <div
      className={`flex flex-col items-center justify-center gap-2 px-6 py-14 text-center text-bc-soft ${className}`}
    >
      <p className="text-sm font-medium text-bc-text">{title}</p>
      {copy.hint && variant !== "error" ? (
        <p className="max-w-xs text-xs text-bc-soft">{copy.hint}</p>
      ) : null}

      {variant === "first-run" && onCreate ? (
        <button type="button" onClick={onCreate} className={`mt-2 ${BUTTON_BASE} ${PRIMARY_BUTTON}`}>
          New broadcast
        </button>
      ) : null}

      {variant === "filtered" && onClear ? (
        <button type="button" onClick={onClear} className={`mt-2 ${BUTTON_BASE} ${SECONDARY_BUTTON}`}>
          Clear filters
        </button>
      ) : null}

      {variant === "error" && onRetry ? (
        <button type="button" onClick={onRetry} className={`mt-2 ${BUTTON_BASE} ${SECONDARY_BUTTON}`}>
          Try again
        </button>
      ) : null}
    </div>
  );
};

export default BroadcastEmpty;
